<script lang="ts">
/**
 * Authenticated user dropdown menu for the sidebar footer.
 *
 * Displays the current user's avatar and name, with a dropdown for
 * navigation (Sandbox) and sign-out. Adapts to collapsed sidebar state
 * by showing only the avatar when collapsed.
 *
 * @example
 * ```vue
 * <template #footer="{ collapsed }">
 *   <UserMenu :collapsed="collapsed" />
 * </template>
 * ```
 */
export interface UserMenuProps {
  /** Whether the sidebar is in collapsed (icon-only) mode. @defaultValue `false` */
  collapsed?: boolean
}
</script>

<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import type { DropdownMenuItem } from "@nuxt/ui"
import { computed } from "vue"

import { useAuthUser } from "@/composables/useAuthUser"
import { ICON } from "@/lib/icons"
import {
  accountMembersPath,
  accountSettingsPath,
  helpPath,
  profilePath,
  superAdminAccountsPath,
} from "@/routes"

defineProps<UserMenuProps>()

const { authUser, userName, avatarSrc, isAccountOwner, isSuperAdmin } = useAuthUser()

const items = computed<DropdownMenuItem[][]>(() => {
  const identity: DropdownMenuItem[] = [{ type: "label", label: userName.value }]

  const navigation: DropdownMenuItem[] = [
    { label: "Profile", icon: ICON.user, to: profilePath() },
    { label: "Help", icon: ICON.help, to: helpPath() },
  ]

  const admin: DropdownMenuItem[] = [
    { label: "Members", icon: ICON.members, to: accountMembersPath() },
  ]
  if (isAccountOwner.value) {
    admin.push({ label: "Account Settings", icon: ICON.settings, to: accountSettingsPath() })
    admin.push({ label: "API Tokens", icon: ICON.apiToken, to: "/account/access_tokens" })
  }
  if (isSuperAdmin.value) {
    admin.push({ label: "Super Admin", icon: ICON.shield, to: superAdminAccountsPath() })
  }
  if (import.meta.env.DEV) {
    admin.push({ label: "Sandbox", icon: ICON.sandbox, to: "/sandbox" })
  }

  const session: DropdownMenuItem[] = [
    {
      label: "Sign out",
      icon: ICON.signOut,
      onSelect() {
        router.delete("/session")
      },
    },
  ]

  return [identity, navigation, admin, session]
})
</script>

<template>
  <UDropdownMenu
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{
      content: collapsed ? 'w-48' : 'w-(--reka-dropdown-menu-trigger-width)',
    }"
  >
    <UButton
      color="neutral"
      variant="ghost"
      block
      :square="collapsed"
      class="data-[state=open]:bg-elevated"
    >
      <UAvatar :src="avatarSrc" :text="authUser?.initials" :alt="authUser?.first_name" size="2xs" />
      <template v-if="!collapsed">
        <span class="truncate">{{ userName }}</span>
        <UIcon :name="ICON.chevronUpDown" class="text-dimmed ml-auto size-4" />
      </template>
    </UButton>
  </UDropdownMenu>
</template>
